// ================================
// PROMTIFY - MAIN JAVASCRIPT
// Welcome to 2050 - Main Page Functionality
// ================================

import './styles/main.css';
import './styles/nova.css';
import * as THREE from 'three';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import '@lottiefiles/dotlottie-web';

gsap.registerPlugin(ScrollTrigger);

// ================================
// GLOBAL STATE
// ================================
const state = {
  isLoading: true,
  audio: null,
  audioPlaying: false,
  threeScene: null,
  customCursor: { x: 0, y: 0 },
  novaOpen: false,
  smartHomeControls: {
    lights: false,
    climate: false,
    security: true
  }
};

// ================================
// LOADER FUNCTIONALITY
// ================================
function initLoader() {
  const loader = document.getElementById('loader');
  const progressFill = document.querySelector('.progress-fill');
  const loaderAnimation = document.querySelector('.loader-animation');
  
  // Initialize Lottie animation
  if (loaderAnimation) {
    try {
      const dotLottie = document.createElement('dotlottie-player');
      dotLottie.setAttribute('src', 'https://lottie.host/4db68bbd-f1d3-4c9c-a3e2-2c0a0d5a5c4e/Y9BnK9hT5e.json');
      dotLottie.setAttribute('background', 'transparent');
      dotLottie.setAttribute('speed', '1');
      dotLottie.setAttribute('loop', 'true');
      dotLottie.setAttribute('autoplay', 'true');
      dotLottie.style.width = '100%';
      dotLottie.style.height = '100%';
      loaderAnimation.appendChild(dotLottie);
    } catch (error) {
      console.log('Lottie animation not available, using fallback');
      loaderAnimation.innerHTML = '<div style="font-size: 6rem; animation: spin 2s linear infinite;">🌀</div>';
    }
  }
  
  // Simulate loading progress
  let progress = 0;
  const interval = setInterval(() => {
    progress += Math.random() * 15;
    if (progress >= 100) {
      progress = 100;
      clearInterval(interval);
      setTimeout(() => {
        hideLoader();
      }, 500);
    }
    if (progressFill) {
      progressFill.style.width = `${progress}%`;
    }
  }, 200);
}

function hideLoader() {
  const loader = document.getElementById('loader');
  const main = document.getElementById('main');
  
  if (loader) {
    loader.classList.add('hidden');
    document.body.classList.remove('loading');
    state.isLoading = false;
    
    // Show main content
    if (main) {
      main.classList.remove('hidden');
    }
    
    // Start animations after loader
    setTimeout(() => {
      initAnimations();
    }, 300);
  }
}

// ================================
// CUSTOM CURSOR
// ================================
function initCustomCursor() {
  const cursor = document.querySelector('.cursor');
  const cursorTrails = [];
  const trailCount = 5;
  
  // Create cursor trails
  for (let i = 0; i < trailCount; i++) {
    const trail = document.createElement('div');
    trail.className = 'cursor-trail';
    document.body.appendChild(trail);
    cursorTrails.push({
      element: trail,
      x: 0,
      y: 0
    });
  }
  
  let mouseX = 0;
  let mouseY = 0;
  let currentX = 0;
  let currentY = 0;
  
  document.addEventListener('mousemove', (e) => {
    mouseX = e.clientX;
    mouseY = e.clientY;
    state.customCursor.x = mouseX;
    state.customCursor.y = mouseY;
  });
  
  // Smooth cursor movement
  function updateCursor() {
    currentX += (mouseX - currentX) * 0.2;
    currentY += (mouseY - currentY) * 0.2;
    
    if (cursor) {
      cursor.style.left = `${currentX}px`;
      cursor.style.top = `${currentY}px`;
    }
    
    // Update trails with delay
    cursorTrails.forEach((trail, index) => {
      const delay = (index + 1) * 0.1;
      trail.x += (currentX - trail.x) * delay;
      trail.y += (currentY - trail.y) * delay;
      trail.element.style.left = `${trail.x}px`;
      trail.element.style.top = `${trail.y}px`;
    });
    
    requestAnimationFrame(updateCursor);
  }
  updateCursor();
  
  // Hover effects
  const interactiveElements = document.querySelectorAll('a, button, .control-toggle, .appliance-btn, .hologram-card, .tech-card, .timeline-event');
  
  interactiveElements.forEach(el => {
    el.addEventListener('mouseenter', () => {
      if (cursor) cursor.classList.add('hover');
    });
    el.addEventListener('mouseleave', () => {
      if (cursor) cursor.classList.remove('hover');
    });
  });
}

// ================================
// THREE.JS BACKGROUND SCENE
// ================================
function initThreeBackground() {
  const canvas = document.getElementById('three-canvas');
  if (!canvas) return;
  
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
  const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
  
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  
  camera.position.z = 5;
  
  // Create particles
  const particlesGeometry = new THREE.BufferGeometry();
  const particleCount = 2000;
  const positions = new Float32Array(particleCount * 3);
  const colors = new Float32Array(particleCount * 3);
  
  const color1 = new THREE.Color(0x00f3ff); // cyan
  const color2 = new THREE.Color(0xff00ff); // magenta
  const color3 = new THREE.Color(0x8000ff); // purple
  
  for (let i = 0; i < particleCount * 3; i += 3) {
    positions[i] = (Math.random() - 0.5) * 20;
    positions[i + 1] = (Math.random() - 0.5) * 20;
    positions[i + 2] = (Math.random() - 0.5) * 10;
    
    // Random color between cyan, magenta, and purple
    const colorChoice = Math.random();
    const chosenColor = colorChoice < 0.33 ? color1 : colorChoice < 0.66 ? color2 : color3;
    colors[i] = chosenColor.r;
    colors[i + 1] = chosenColor.g;
    colors[i + 2] = chosenColor.b;
  }
  
  particlesGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  particlesGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  
  const particlesMaterial = new THREE.PointsMaterial({
    size: 0.05,
    vertexColors: true,
    transparent: true,
    opacity: 0.8,
    blending: THREE.AdditiveBlending
  });
  
  const particles = new THREE.Points(particlesGeometry, particlesMaterial);
  scene.add(particles);
  
  // Animation
  function animate() {
    requestAnimationFrame(animate);
    
    particles.rotation.x += 0.0003;
    particles.rotation.y += 0.0005;
    
    // Move particles based on scroll
    const scrollProgress = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight);
    particles.position.y = scrollProgress * 3;
    
    renderer.render(scene, camera);
  }
  animate();
  
  // Resize handler
  window.addEventListener('resize', () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
  });
  
  state.threeScene = { scene, camera, renderer, particles };
}

// ================================
// PARALLAX EFFECTS
// ================================
function initParallax() {
  const parallaxLayers = document.querySelectorAll('.parallax-layer');
  
  window.addEventListener('scroll', () => {
    const scrolled = window.scrollY;
    
    parallaxLayers.forEach((layer, index) => {
      const speed = (index + 1) * 0.1;
      const yPos = -(scrolled * speed);
      layer.style.transform = `translateY(${yPos}px)`;
    });
  });
  
  // Mouse move parallax for hero
  const hero = document.querySelector('.hero');
  if (hero) {
    document.addEventListener('mousemove', (e) => {
      const mouseX = e.clientX / window.innerWidth - 0.5;
      const mouseY = e.clientY / window.innerHeight - 0.5;
      
      parallaxLayers.forEach((layer, index) => {
        const depth = (index + 1) * 20;
        const moveX = mouseX * depth;
        const moveY = mouseY * depth;
        layer.style.transform = `translate(${moveX}px, ${moveY}px)`;
      });
    });
  }
}

// ================================
// SMART HOME CONTROLS
// ================================
function initSmartHomeControls() {
  const controls = document.querySelectorAll('.control-toggle input[type="checkbox"]');
  
  controls.forEach(control => {
    const controlId = control.id;
    
    // Set initial state
    if (state.smartHomeControls.hasOwnProperty(controlId)) {
      control.checked = state.smartHomeControls[controlId];
    }
    
    control.addEventListener('change', (e) => {
      const isChecked = e.target.checked;
      state.smartHomeControls[controlId] = isChecked;
      
      // Add animation effect
      const controlItem = e.target.closest('.control-item');
      if (controlItem) {
        gsap.fromTo(controlItem, 
          { scale: 1 },
          { scale: 1.05, duration: 0.2, yoyo: true, repeat: 1 }
        );
      }
      
      console.log(`${controlId} is now ${isChecked ? 'ON' : 'OFF'}`);
    });
  });
  
  // Appliance buttons
  const applianceButtons = document.querySelectorAll('.appliance-btn');
  applianceButtons.forEach(btn => {
    btn.addEventListener('click', function() {
      gsap.fromTo(this,
        { scale: 1 },
        { scale: 0.9, duration: 0.1, yoyo: true, repeat: 1 }
      );
      
      // Toggle active state
      this.classList.toggle('active');
      console.log('Appliance toggled:', this.textContent);
    });
  });
}

// ================================
// HOLOGRAM CARDS
// ================================
function initHologramCards() {
  const cards = document.querySelectorAll('.hologram-card, .tech-card');
  
  cards.forEach(card => {
    card.addEventListener('mouseenter', function() {
      gsap.to(this, {
        scale: 1.05,
        duration: 0.3,
        ease: 'power2.out'
      });
    });
    
    card.addEventListener('mouseleave', function() {
      gsap.to(this, {
        scale: 1,
        duration: 0.3,
        ease: 'power2.out'
      });
    });
  });
}

// ================================
// VR PORTAL
// ================================
function initVRPortal() {
  const portal = document.querySelector('.vr-portal');
  if (!portal) return;
  
  portal.addEventListener('mouseenter', function() {
    const rings = this.querySelectorAll('.portal-ring');
    rings.forEach((ring, index) => {
      gsap.to(ring, {
        scale: 1.2,
        duration: 0.5,
        delay: index * 0.1,
        ease: 'power2.out'
      });
    });
  });
  
  portal.addEventListener('mouseleave', function() {
    const rings = this.querySelectorAll('.portal-ring');
    rings.forEach((ring, index) => {
      gsap.to(ring, {
        scale: 1,
        duration: 0.5,
        delay: index * 0.1,
        ease: 'power2.out'
      });
    });
  });
}

// ================================
// TIMELINE ANIMATION
// ================================
function initTimeline() {
  const timeline = document.querySelector('.daily-timeline-container');
  if (!timeline) return;
  
  const events = document.querySelectorAll('.timeline-event');
  const dots = document.querySelectorAll('.timeline-dot');
  
  events.forEach((event, index) => {
    event.addEventListener('mouseenter', function() {
      gsap.to(this, {
        y: -10,
        boxShadow: '0 15px 40px rgba(0, 243, 255, 0.5)',
        duration: 0.3
      });
      
      if (dots[index]) {
        gsap.to(dots[index], {
          scale: 1.5,
          duration: 0.3
        });
      }
    });
    
    event.addEventListener('mouseleave', function() {
      gsap.to(this, {
        y: 0,
        boxShadow: '0 5px 20px rgba(0, 243, 255, 0.2)',
        duration: 0.3
      });
      
      if (dots[index]) {
        gsap.to(dots[index], {
          scale: 1,
          duration: 0.3
        });
      }
    });
  });
}

// ================================
// NOVA AI CHATBOT
// ================================
function initNovaChatbot() {
  const fab = document.querySelector('.nova-fab');
  const chatbot = document.querySelector('.nova-chatbot');
  const closeBtn = document.querySelector('.chatbot-close');
  const sendBtn = document.querySelector('.send-button');
  const input = document.querySelector('.chatbot-input input');
  const messagesContainer = document.querySelector('.chatbot-messages');
  const suggestions = document.querySelectorAll('.suggestion-chip');
  
  if (!fab || !chatbot) return;
  
  // Toggle chatbot
  fab.addEventListener('click', () => {
    state.novaOpen = !state.novaOpen;
    chatbot.classList.toggle('open');
    fab.classList.toggle('open');
    
    if (state.novaOpen) {
      input?.focus();
      addMessage('bot', 'Hello! I\'m Nova, your AI assistant for 2050. How can I help you today?');
    }
  });
  
  closeBtn?.addEventListener('click', () => {
    state.novaOpen = false;
    chatbot.classList.remove('open');
    fab.classList.remove('open');
  });
  
  // Send message
  function sendMessage() {
    const message = input?.value.trim();
    if (!message) return;
    
    addMessage('user', message);
    input.value = '';
    
    // Simulate bot response
    setTimeout(() => {
      addTypingIndicator();
      setTimeout(() => {
        removeTypingIndicator();
        const responses = [
          'That\'s an interesting question! In 2050, technology has advanced significantly.',
          'I can help you with that. Let me access the neural database...',
          'Great question! The future of technology is incredibly exciting.',
          'I\'m processing your request through quantum computing nodes...',
          'Fascinating! Let me share some insights about that.'
        ];
        const randomResponse = responses[Math.floor(Math.random() * responses.length)];
        addMessage('bot', randomResponse);
      }, 1500);
    }, 500);
  }
  
  sendBtn?.addEventListener('click', sendMessage);
  input?.addEventListener('keypress', (e) => {
    if (e.key === 'Enter') sendMessage();
  });
  
  // Suggestion chips
  suggestions.forEach(chip => {
    chip.addEventListener('click', function() {
      const message = this.textContent;
      if (input) input.value = message;
      sendMessage();
    });
  });
  
  // Add message to chat
  function addMessage(type, text) {
    if (!messagesContainer) return;
    
    const messageDiv = document.createElement('div');
    messageDiv.className = `message ${type}`;
    
    const time = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    
    messageDiv.innerHTML = `
      <div class="message-avatar">${type === 'bot' ? '🤖' : '👤'}</div>
      <div class="message-bubble">
        ${text}
        <span class="message-time">${time}</span>
      </div>
    `;
    
    messagesContainer.appendChild(messageDiv);
    messagesContainer.scrollTop = messagesContainer.scrollHeight;
    
    // Animate message
    gsap.fromTo(messageDiv,
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.4 }
    );
  }
  
  function addTypingIndicator() {
    if (!messagesContainer) return;
    
    const typingDiv = document.createElement('div');
    typingDiv.className = 'message bot typing-message';
    typingDiv.innerHTML = `
      <div class="message-avatar">🤖</div>
      <div class="typing-indicator">
        <div class="typing-dot"></div>
        <div class="typing-dot"></div>
        <div class="typing-dot"></div>
      </div>
    `;
    messagesContainer.appendChild(typingDiv);
    messagesContainer.scrollTop = messagesContainer.scrollHeight;
  }
  
  function removeTypingIndicator() {
    const typingMsg = document.querySelector('.typing-message');
    if (typingMsg) typingMsg.remove();
  }
}

// ================================
// AUDIO TOGGLE
// ================================
function initAudioToggle() {
  const audioToggle = document.querySelector('.audio-toggle');
  if (!audioToggle) return;
  
  // Create ambient audio (optional - you can add actual audio file)
  // state.audio = new Audio('/path/to/ambient-sound.mp3');
  // state.audio.loop = true;
  
  audioToggle.addEventListener('click', function() {
    state.audioPlaying = !state.audioPlaying;
    this.classList.toggle('muted');
    
    if (state.audio) {
      if (state.audioPlaying) {
        state.audio.play();
        this.innerHTML = '🔊';
      } else {
        state.audio.pause();
        this.innerHTML = '🔇';
      }
    }
    
    console.log('Audio:', state.audioPlaying ? 'ON' : 'OFF');
  });
}

// ================================
// GSAP SCROLL ANIMATIONS
// ================================
function initAnimations() {
  // Fade in sections
  const sections = document.querySelectorAll('section');
  sections.forEach(section => {
    gsap.fromTo(section,
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        scrollTrigger: {
          trigger: section,
          start: 'top 80%',
          end: 'top 50%',
          scrub: 1
        }
      }
    );
  });
  
  // Animate smart panels
  const panels = document.querySelectorAll('.smart-panel');
  panels.forEach((panel, index) => {
    gsap.fromTo(panel,
      { opacity: 0, scale: 0.8, rotateY: -20 },
      {
        opacity: 1,
        scale: 1,
        rotateY: 0,
        duration: 0.8,
        delay: index * 0.1,
        scrollTrigger: {
          trigger: panel,
          start: 'top 85%',
          toggleActions: 'play none none reverse'
        }
      }
    );
  });
  
  // Animate hologram cards
  const hologramCards = document.querySelectorAll('.hologram-card');
  hologramCards.forEach((card, index) => {
    gsap.fromTo(card,
      { opacity: 0, y: 100, rotateX: 45 },
      {
        opacity: 1,
        y: 0,
        rotateX: 0,
        duration: 1,
        delay: index * 0.2,
        scrollTrigger: {
          trigger: card,
          start: 'top 85%',
          toggleActions: 'play none none reverse'
        }
      }
    );
  });
  
  // Animate tech cards
  const techCards = document.querySelectorAll('.tech-card');
  techCards.forEach((card, index) => {
    gsap.fromTo(card,
      { opacity: 0, scale: 0.5, rotateY: 180 },
      {
        opacity: 1,
        scale: 1,
        rotateY: 0,
        duration: 1,
        delay: index * 0.15,
        scrollTrigger: {
          trigger: card,
          start: 'top 85%',
          toggleActions: 'play none none reverse'
        }
      }
    );
  });
  
  // Animate timeline events
  const timelineEvents = document.querySelectorAll('.timeline-event');
  timelineEvents.forEach((event, index) => {
    gsap.fromTo(event,
      { opacity: 0, x: index % 2 === 0 ? -100 : 100 },
      {
        opacity: 1,
        x: 0,
        duration: 0.8,
        delay: index * 0.1,
        scrollTrigger: {
          trigger: event,
          start: 'top 85%',
          toggleActions: 'play none none reverse'
        }
      }
    );
  });
}

// ================================
// SMOOTH SCROLL
// ================================
function initSmoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function(e) {
      e.preventDefault();
      const target = document.querySelector(this.getAttribute('href'));
      if (target) {
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    });
  });
}

// ================================
// INITIALIZATION
// ================================
function init() {
  console.log('🚀 Initializing Promtify - Welcome to 2050!');
  
  // Start loader
  document.body.classList.add('loading');
  initLoader();
  
  // Initialize features
  initCustomCursor();
  initThreeBackground();
  initParallax();
  initSmartHomeControls();
  initHologramCards();
  initVRPortal();
  initTimeline();
  initNovaChatbot();
  initAudioToggle();
  initSmoothScroll();
  
  console.log('✨ Promtify initialized successfully!');
}

// Start when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Export for potential use in other modules
export { state, initLoader, initAnimations };
